import React, {useEffect, useRef, useState} from "react";
import {getJSON, startFreshDemo, page, primaryBtn, darkBtn} from "./ui";
import {ZedSays, ZedVoiced} from "./zed";

// Kickoff's finale: Zed's verdict on everyone, in his voice. The host's phone (or
// laptop) plays it; everyone sees the words.
export const RoastPhase = ({state, history}) => {
    const [roast, setRoast] = useState(null);
    const [playing, setPlaying] = useState(false);
    const [takes, setTakes] = useState(0); // each play restarts the synced face and words
    const audioRef = useRef(null);

    useEffect(() => {
        let live = true;
        const tick = () => getJSON('/demo/roast').then(r => { if (live && r) setRoast(r); });
        tick();
        const id = setInterval(tick, 1000);
        return () => { live = false; clearInterval(id); };
    }, []);

    const ready = roast && roast.status === 'ready';
    const play = () => {
        if (!roast.audio_url) return;
        if (!audioRef.current) audioRef.current = new Audio(roast.audio_url);
        audioRef.current.currentTime = 0;
        audioRef.current.onended = () => setPlaying(false);
        audioRef.current.play().then(() => { setTakes(n => n + 1); setPlaying(true); }).catch(() => {});
    };

    if (roast && roast.status === 'failed') return <div style={{...page, alignItems: 'center', justifyContent: 'center', textAlign: 'center'}}>
        <div style={{fontSize: 26, fontWeight: 800}}>Zed has declined to comment.</div>
        {state.is_host && <button style={darkBtn} onClick={() => startFreshDemo(history, 'kickoff')}>Run it again</button>}
    </div>;

    if (!ready) return <div style={{...page, justifyContent: 'center'}}>
        <ZedSays line="Reviewing the evidence." />
    </div>;

    // The host's phone is the stage: tap play, and his voice, mouth and the typed words run together. Guests
    // are sent to the host's phone, so everyone watches the finale together. Without voice timing (voice
    // failed), everyone reads the verdict as typed paragraphs.
    const timing = roast.timing && roast.timing.chars && roast.audio_url ? roast.timing : null;
    if (timing && !state.is_host) return <div style={{...page, justifyContent: 'center', textAlign: 'center', gap: 20}}>
        <ZedSays line={`My verdict is on ${roast.host_name || "the host"}'s phone. Go look.`} />
        <div style={{fontSize: 64}}>👀</div>
    </div>;

    return <div style={{...page, gap: 16}}>
        {timing
            ? <>
                <div style={{textAlign: 'center', fontFamily: "'Doto', monospace", fontWeight: 900, fontSize: 26, letterSpacing: 2, color: '#ff3d14',
                    textShadow: '0 0 6px rgba(255,61,20,0.85)'}}>ZED'S VERDICT</div>
                <ZedVoiced key={takes} chars={timing.chars} times={timing.times} audio={playing ? audioRef.current : null} />
                <button style={primaryBtn} onClick={play}>{playing ? 'Play it again' : "▶ Play Zed's verdict"}</button>
            </>
            : <Verdict paragraphs={roast.text.split(/\n+/).filter(Boolean)} />}
        {state.is_host && <button style={{...darkBtn, marginTop: 'auto'}} onClick={() => startFreshDemo(history, 'kickoff')}>Run it again</button>}
    </div>;
};

// Zed delivers the verdict a paragraph at a time, his face typing each one out; a tap skips ahead.
const Verdict = ({paragraphs}) => {
    const [i, setI] = useState(0);
    const [done, setDone] = useState(false);
    const next = () => { if (i < paragraphs.length - 1) { setI(i + 1); setDone(false); } };
    useEffect(() => {
        if (!done || i >= paragraphs.length - 1) return undefined;
        const t = setTimeout(next, 1800);
        return () => clearTimeout(t);
    }, [done, i]); // eslint-disable-line react-hooks/exhaustive-deps
    return <div onClick={next} style={{display: 'flex', flexDirection: 'column', gap: 10}}>
        <div style={{textAlign: 'center', fontFamily: "'Doto', monospace", fontWeight: 900, fontSize: 26, letterSpacing: 2, color: '#ff3d14',
            textShadow: '0 0 6px rgba(255,61,20,0.85)'}}>ZED'S VERDICT</div>
        <ZedSays speech line={paragraphs[i]} onDone={() => setDone(true)} />
        <div style={{textAlign: 'center', opacity: 0.45, fontSize: 14}}>{i + 1} / {paragraphs.length}</div>
    </div>;
};
